/**
 * Item API 함수들
 */

import { AuthContextType } from "../auth/AuthContext";
import { apiWithTimeout } from "../../shared/api/timeout";
import {
    apiHost,
    isApiConnectionFailure,
    isPayloadError,
    getErrorMessage,
} from "../../shared/api/helpers";
import type { ApiErrorSetter } from "../../shared/api/helpers";

const MISSING_ORIGIN_PRODUCT_ID_MESSAGE = "원상품 ID를 조회해 주세요.";

export type ActivationFailureItem = {
    id: string;
    shop_item_id: string;
    errors: Record<string, string>;
};

function getFirstItemErrorMessage(value: unknown): string {
    if (Array.isArray(value)) {
        return value.length > 0 ? getFirstItemErrorMessage(value[0]) : "";
    }
    if (value === null || value === undefined) {
        return "";
    }
    return String(value);
}

function applyItemApiErrors(
    errorData: any,
    payload: object,
    setError?: (...args: any) => void,
    setErrorMessage?: (arg?: any) => void,
    fallbackMessage?: string,
) {
    const wrappedErrors = errorData?.errors;
    if (wrappedErrors && typeof wrappedErrors === "object" && !Array.isArray(wrappedErrors)) {
        let hasFieldError = false;
        let globalMessage = "";
        for (const [key, value] of Object.entries(wrappedErrors)) {
            const message = getFirstItemErrorMessage(value);
            if (!message) {
                continue;
            }
            if (key === "non_field_errors") {
                globalMessage ||= message;
            } else if (setError) {
                setError(key, { type: "custom", message });
                hasFieldError = true;
            }
        }
        const hasMissingOriginProductId = Boolean(wrappedErrors.shop_item_gid);
        if (hasMissingOriginProductId && setErrorMessage) {
            setErrorMessage(MISSING_ORIGIN_PRODUCT_ID_MESSAGE);
        } else if (globalMessage && setErrorMessage) {
            setErrorMessage(globalMessage);
        } else if (!hasFieldError && setErrorMessage) {
            setErrorMessage(errorData.message || fallbackMessage);
        }
        return true;
    }

    if (isPayloadError(errorData, payload) && setError) {
        for (const [key, value] of Object.entries(errorData)) {
            setError(key, { type: "custom", message: getFirstItemErrorMessage(value) });
        }
        return true;
    }

    setErrorMessage?.(errorData?.message || fallbackMessage);
    return false;
}

export interface fetchItemListOpts {
    auth: AuthContextType,
    setErrorMessage?: ApiErrorSetter;
    setResult?: (arg: any) => void;
    params?: { compare_site?: number;[key: string]: any };
}

export async function fetchItemList(opts: fetchItemListOpts) {
    const { auth, setErrorMessage, setResult, params } = opts;
    try {
        let url = `${apiHost}/api/items/`;
        if (params) {
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    searchParams.append(key, value.toString());
                }
            });
            if (searchParams.toString()) {
                url += `?${searchParams.toString()}`;
            }
        }
        const response = await apiWithTimeout.quick.get(url);
        if (response.data && setResult) {
            setResult(response.data);
        }
        setErrorMessage?.("", false);
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message, isApiConnectionFailure(error));
        } else {
            console.error(message);
        }
    }
}


export async function createItem(opts: {
    auth: AuthContextType,
    payload: any,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...args: any) => void;
}) {
    const { payload, auth, setErrorMessage, setProcessMessage, setResult, setError } = opts;
    try {
        await apiWithTimeout.normal.post(`${apiHost}/api/items/`, payload);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }
        const errorMessage = getErrorMessage(error);
        const errorData = error.response?.data ?? {};
        applyItemApiErrors(errorData, payload, setError, setErrorMessage, errorMessage);
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function retrieveItem(opts: {
    auth: AuthContextType,
    id?: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, setErrorMessage, setResult } = opts;
    try {
        let url = `${apiHost}/api/items/${opts.id}/`;
        const response = await apiWithTimeout.quick.get(url);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}



export async function updateItem(opts: {
    auth: AuthContextType,
    id: string,
    payload: any,
    setErrorMessage: any,
    setProcessMessage: any,
    setResult: any,
    setError: any
}) {
    const { auth, id, payload, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        await apiWithTimeout.normal.patch(`${apiHost}/api/items/${id}/`, payload);
        // await updateShopTestedAt(auth, payload.shop_id, true);
        setResult && setResult();
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }
        const errorData = error.response?.data ?? {};
        const errorMessage = getErrorMessage(error);
        applyItemApiErrors(errorData, payload, setError, setErrorMessage, errorMessage);
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export type ItemPlacementAction =
    | { action: "swap"; item_id: string; other_item_id: string }
    | { action: "move_group"; item_id: string; itemgroup_id: string | null };

export async function updateItemPlacement(auth: AuthContextType, payload: ItemPlacementAction): Promise<void> {
    try {
        await apiWithTimeout.normal.post(`${apiHost}/api/items/placement/`, payload);
    } catch (error: any) {
        if (error.response?.status === 401) auth?.logout();
        throw new Error(error.response?.data?.message || getErrorMessage(error));
    }
}

export interface ItemAmounts {
    price: number;
    discount: number;
}

export async function fetchCurrentItemAmounts(auth: AuthContextType, id: string): Promise<ItemAmounts & { synced: boolean }> {
    try {
        const response = await apiWithTimeout.long.post(`${apiHost}/api/items/${id}/amounts/`);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 401) auth?.logout();
        throw new Error(error.response?.data?.message || getErrorMessage(error));
    }
}

export async function updateItemAmount(
    auth: AuthContextType,
    id: string,
    field: "price" | "discount",
    value: number,
): Promise<ItemAmounts> {
    try {
        const response = await apiWithTimeout.long.patch(`${apiHost}/api/items/${id}/amounts/`, { field, value });
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 401) auth?.logout();
        throw new Error(error.response?.data?.message || getErrorMessage(error));
    }
}

export async function deleteItem(opts: {
    auth: AuthContextType,
    id: string,
    setErrorMessage?: (arg?: any) => void,
    setProcessMessage?: (arg?: any) => void,
    setResult?: (arg?: any) => void,
}) {
    const { auth, id, setErrorMessage, setProcessMessage, setResult } = opts;
    try {
        setProcessMessage && setProcessMessage('삭제 중입니다...');
        await apiWithTimeout.normal.delete(`${apiHost}/api/items/${id}/`);
        setResult && setResult();
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    } finally {
        setProcessMessage && setProcessMessage(null);
    }
}

export async function duplicateItem(opts: {
    auth: AuthContextType,
    id: string,
    setErrorMessage?: (arg?: any) => void,
    setProcessMessage?: (arg?: any) => void,
    setResult?: (id: string) => void,
}) {
    const { auth, id, setErrorMessage, setProcessMessage, setResult } = opts;
    try {
        setProcessMessage && setProcessMessage('복제 중입니다...');
        const response = await apiWithTimeout.normal.post(`${apiHost}/api/items/${id}/duplicate/`);
        const duplicatedId = response.data?.item?.id;
        if (duplicatedId) {
            setResult && setResult(duplicatedId);
        } else if (setErrorMessage) {
            setErrorMessage('복제된 상품 정보를 확인할 수 없습니다.');
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    } finally {
        setProcessMessage && setProcessMessage(null);
    }
}

export async function retrieveItemDuplicates(opts: {
    auth: AuthContextType,
    id: string,
    setErrorMessage?: (arg?: any) => void,
    setResult?: (items: Array<{ id: string, name: string, is_active: boolean }>) => void,
}) {
    const { auth, id, setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/items/${id}/duplicates/`);
        setResult && setResult(response.data?.items || []);
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
            return;
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function updateItems(opts: {
    auth: AuthContextType,
    ids: string[],
    payload: any,
    setErrorMessage?: (message: string) => void,
    setProcessMessage?: (message: string | null) => void,
    setResult?: (message?: string) => void,
    setError?: (key: string, value: { type: string; message: unknown }) => void,
    setActivationFailures?: (items: ActivationFailureItem[]) => void,
}) {
    const { auth, ids, payload, setErrorMessage, setProcessMessage, setResult, setError, setActivationFailures } = opts;

    try {
        const result = await apiWithTimeout.normal.post(`${apiHost}/api/items/batch/`, { ...payload, item_ids: ids });
        setResult?.(result.data.message);
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }
        console.log(error.response)
        const errorData = error.response?.data ?? {};
        if (payload.action === 'activate' && Array.isArray(errorData.invalid_items)) {
            setActivationFailures?.(errorData.invalid_items);
        }
        const errorMessage =
            errorData.error
            || errorData.message
            || "시스템 오류입니다. 잠시 후 다시 시도하세요";
        if (isPayloadError(errorData, payload) && setError) {
            for (const [key, value] of Object.entries(errorData)) {
                setError(key, { type: "custom", message: value });
            }
        } else {
            setErrorMessage?.(errorMessage);
        }
    } finally {
        setProcessMessage?.(null);
    }
}
